<script lang="ts" setup>
import type { ICTABlockProps } from './ts/types'
import { ECTABlockButtonAlignment } from './ts/types'
import { useDrawer } from '@/cms-ui/composables/useDrawer'
import { useClickTracking } from '@/cms-ui/composables/useAnalytics'
import { EClickElement } from '@/cms-ui/utils/dataLayer'

const props = withDefaults(defineProps<ICTABlockProps>(), {
    alignment: ECTABlockButtonAlignment.CENTER,
    alignmentDesktop: ECTABlockButtonAlignment.LEFT,
})

const alignment = {
    [ECTABlockButtonAlignment.LEFT]: 'items-start',
    [ECTABlockButtonAlignment.CENTER]: 'items-center',
    [ECTABlockButtonAlignment.RIGHT]: 'items-end',
}

const alignmentDesktop = {
    [ECTABlockButtonAlignment.LEFT]: 'lg:items-start',
    [ECTABlockButtonAlignment.CENTER]: 'lg:items-center',
    [ECTABlockButtonAlignment.RIGHT]: 'lg:items-end',
}

const { openDrawer } = useDrawer()
</script>

<template>
    <div
        class="gap-md flex flex-col"
        :class="[alignment[props.alignment], alignmentDesktop[props.alignmentDesktop]]"
    >
        <div
            class="gap-horizontal-button-group flex max-w-full flex-wrap"
            :class="{
                'flex-row justify-center lg:justify-start': props.inline,
                'flex-col items-start': !props.inline && !props.forceInline,
                'flex-row !flex-nowrap': props.forceInline,
            }"
        >
            <BaseButton
                v-if="props.ctaButton1"
                :variant="props.ctaButton1.visualVariant"
                :trailing-icon="props.ctaButton1.icon"
                :size="props.ctaButton1.size"
                :dark="props.dark"
                :href="props.ctaButton1?.action?.url || undefined"
                :target="props.ctaButton1.action?.openInNewTab ? '_blank' : '_self'"
                data-test="CTA_BLOCK_BUTTON1"
                class="max-w-full"
                @click="[
                    useClickTracking({
                        label: props.ctaButton1.label,
                        clickPath: props.ctaButton1?.action?.url || '',
                        clickElement: EClickElement.BUTTON,
                        componentName: props.componentName || 'CTABlock',
                        componentCategory: props.componentCategory || 'CTABlock',
                    }),
                    openDrawer(props.ctaButton1?.action?.drawer, page),
                ]"
            >
                {{ props.ctaButton1.label }}
            </BaseButton>
            <BaseButton
                v-if="props.ctaButton2"
                :variant="props.ctaButton2.visualVariant"
                :trailing-icon="props.ctaButton2.icon"
                :size="props.ctaButton2.size"
                :dark="props.dark"
                :href="props.ctaButton2.action?.url || undefined"
                :target="props.ctaButton2.action?.openInNewTab ? '_blank' : '_self'"
                class="max-w-full"
                data-test="CTA_BLOCK_BUTTON2"
                @click="[
                    useClickTracking({
                        label: props.ctaButton2.label,
                        clickPath: props.ctaButton2?.action?.url || '',
                        clickElement: EClickElement.BUTTON,
                        componentName: props.componentName || 'CTABlock',
                        componentCategory: props.componentCategory || 'CTABlock',
                    }),
                    openDrawer(props.ctaButton2?.action?.drawer, page),
                ]"
            >
                {{ props.ctaButton2.label }}
            </BaseButton>
        </div>

        <div v-if="props.ctaLink">
            <VLink
                id="CTA_BLOCK_LINK"
                :href="props.ctaLink?.action?.url"
                class="text-comp-high-contrast"
                :size="props.ctaLink.size"
                :dark="props.dark"
                data-test="CTA_BLOCK_LINK"
                :download="props.ctaLink.action?.downloadedAsset?.asset?.links?.site?.href"
                :target="props.ctaLink.action?.openInNewTab ? '_blank' : '_self'"
                @click="[
                    useClickTracking({
                        label: props.ctaLink.label,
                        clickPath: props.ctaLink?.action?.url || '',
                        clickElement: EClickElement.LINK,
                        componentName: props.componentName || 'CTABlock',
                        componentCategory: props.componentCategory || 'CTABlock',
                    }),
                    openDrawer(props.ctaLink?.action?.drawer, page),
                ]"
            >
                {{ props.ctaLink.label }}
                <VIcon
                    v-if="props.ctaLink.visualVariant === 'chevron'"
                    name="chevron-right"
                    size="SMALL"
                    data-test="CTA_BLOCK_LINK-CHEVRON"
                />
            </VLink>
        </div>
    </div>
</template>
